import React from 'react';
import { Spinner, useToggle } from 'shared/hooks';
import { shiftAlerts, useAlert } from 'shared/components/alerts';
import { ApplicationDetails } from '@types';
import { useInvalidateQuery } from 'shared/hooks/query-hooks/useInvalidateQuery';
import { Box, Dialog, DialogActions, DialogContent } from '@mui/material';
import _ from 'lodash';
import { LwButton } from 'redesign';
import { QUERY_KEYS } from 'api/query-keys';
import { transformShiftApplication } from './helpers/helpers';
import type { FlexWorker, ShiftApplication } from './bulk-accept-shift-dialog.types';
import { BulkAcceptShiftDialogForm } from './bulk-accept-shift-dialog-form';
import { BulkAcceptMutationType } from '../../query-hooks/use-bulk-accept-shift-applications.types';

const useBulkAcceptApplicationsDialog = (mutation: BulkAcceptMutationType) => {
  const { on: isDialogOpen, setOn: openDialog, setOff: closeDialog } = useToggle(false);
  const [flexWorker, setFlexWorker] = React.useState<FlexWorker>();
  const [shiftApplications, setShiftApplications] = React.useState<{
    current: ShiftApplication;
    rest: ShiftApplication[];
  }>();
  const { alertSuccess, alertError } = useAlert();
  const invalidateQuery = useInvalidateQuery();
  const bulkAcceptShiftApplicationsMutation = mutation({
    onSuccess: (result) => {
      closeDialog();
      const succeeded = result.filter((item) => item.status === 'success');
      alertSuccess(shiftAlerts.success.bulkCreated(succeeded.length, result.length));

      if (!shiftApplications) {
        return;
      }
      invalidateQuery(QUERY_KEYS.shifts());
    },
    onError: (e) => alertError(e),
  });

  const handleBulkAcceptShiftApplications = (shiftApplicationIds: string[]) => {
    if (!(flexWorker && shiftApplications)) {
      return;
    }

    bulkAcceptShiftApplicationsMutation.mutate({
      flexWorkerId: flexWorker.id,
      shiftApplicationIds,
    });
  };

  const Element =
    flexWorker && shiftApplications ? (
      <Dialog maxWidth="sm" onClose={closeDialog} open={isDialogOpen}>
        <DialogContent>
          <BulkAcceptShiftDialogForm
            id="shift-application-approval-bulk-form"
            flexWorker={flexWorker}
            shiftApplications={shiftApplications}
            onSubmit={handleBulkAcceptShiftApplications}
          />
        </DialogContent>
        <Box
          component={DialogActions}
          display="flex"
          style={{ gap: '32px', justifyContent: 'center' }}
        >
          <LwButton autoFocus color="secondary" onClick={closeDialog}>
            Annuleren
          </LwButton>
          <LwButton type="submit" form="shift-application-approval-bulk-form">
            Bevestigen
            {bulkAcceptShiftApplicationsMutation.status === 'loading' ? <Spinner /> : null}
          </LwButton>
        </Box>
      </Dialog>
    ) : null;

  function open(
    flexWorker: FlexWorker,
    shiftApplicationId: string,
    shiftApplications: ApplicationDetails['allApplications']
  ) {
    const applicationsWithIntervals = shiftApplications.map(transformShiftApplication);
    const [[currentApplication], otherApplications] = _.partition(
      applicationsWithIntervals,
      (a) => a.id === shiftApplicationId
    );

    setFlexWorker(flexWorker);
    setShiftApplications({
      current: currentApplication,
      rest: otherApplications,
    });

    openDialog();
  }

  return { Element, open, close: closeDialog };
};

export { useBulkAcceptApplicationsDialog };
